import * as React from "react";
import clsx from "clsx";
import { useNavigate } from "@tanstack/react-router";
import { Award, GraduationCap, LayoutTemplate, RotateCcw, Save, PanelTop, IdCard, ScrollText } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { LocalizedField } from "../../../ui/components/LocalizedField";
import { MediaUploader } from "../../../ui/components/MediaUploader";
import type { MediaEntry } from "../../../ui/components/MediaUploader";
import { UnsavedChangesChip } from "../../../ui/components/UnsavedChangesChip";
import type { FloatingAction } from "../../../ui/components/FloatingActionBar";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import { useAuth } from "../../lib/auth/hooks";
import { getAuthCookie } from "../../lib/auth/session-cookie";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { useI18n } from "../../lib/i18n";
import { useSectionCollapse } from "../../hooks/useSectionCollapse";
import {
  fetchCertificationModule,
  deleteCertificationTraining,
  updateCertificationModule,
  updateCertificationTraining,
  requestHeroImageUpload,
  requestTrophyImageUpload,
} from "../../lib/api/certifications";
import type { CertificationModuleDetail, CertificationTraining } from "../../lib/api/certifications";
import { batchPatchLocalizationDefinitions } from "../../lib/api/localization";
import { TrainingFormModal } from "./TrainingFormModal";
import { useCertificationContentDefinitions, invalidateCertificationContentDefinitionsCache } from "../../lib/i18n/useCertificationContentDefinitions";
import {
  collectDirtyUpdates,
  ensureLocMigrationDirty,
  isContentLocKey,
  locDraftFromStored,
  moduleCertificateCourseTextKey,
  moduleCertificateHeaderTextKey,
  moduleDescriptionKey,
  moduleTitleKey,
  setLocValue,
  type LocFieldDraft,
} from "../../lib/i18n/contentKeys";
import { getLocDisplayValue } from "../../lib/i18n/trainingLocalizedDraft";
import {
  suppressCertificationLocRemoteRevision,
  useCertificationLocLanguages,
} from "../../lib/i18n/useCertificationLocLanguages";
import { useCertificationModuleRemoteRevision, suppressCertificationModuleRemoteRevision } from "../../lib/i18n/useCertificationModuleRemoteRevision";
import { CertificationCourseCard } from "../certification/CertificationCourseCard";
import { ListViewUserCertificationCard } from "../../../ui/components/ListView/components/ListViewUserCertificationCard";
import { CoursePagePreview } from "./CoursePagePreview";
import { CoursePreviewHoverButton } from "./CoursePreviewHoverButton";
import { CertificatePreview } from "./CertificatePreview";
import { CourseTrainingsStrip } from "./CourseTrainingsStrip";
import {
  isQuestionsDraftDirty,
  persistTrainingTextDraft,
  type CourseTrainingEditorState,
  type TrainingDraftSnapshot,
} from "./CourseTrainingDetail";
import { CourseTrainingDetailSlider } from "./CourseTrainingDetailSlider";
import * as layout from "../organizations/OrganizationEditPage.css";
import * as courseStyles from "./ModuleDetailPage.css";

const SECTION_IDS = {
  course: "module-course",
} as const;

interface CourseFormValues {
  slug: string;
  heroImageUrl: string;
  trophyImageUrl: string;
  isActive: boolean;
}

function moduleToCourseForm(m: CertificationModuleDetail): CourseFormValues {
  return {
    slug: m.slug,
    heroImageUrl: m.heroImageUrl ?? "",
    trophyImageUrl: m.trophyImageUrl ?? "",
    isActive: m.isActive,
  };
}

async function uploadModuleImageFile(
  accessToken: string,
  file: File,
  failMessage: string,
  requestUpload: typeof requestHeroImageUpload,
): Promise<string> {
  const instructions = await requestUpload(accessToken, file.type);
  const isPresigned = instructions.uploadUrl.includes("X-Amz-Signature");
  const headers: Record<string, string> = { "Content-Type": file.type };
  if (!isPresigned) {
    if (instructions.key) headers["X-Avatar-Key"] = instructions.key;
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  }
  const res = await fetch(instructions.uploadUrl, { method: "PUT", body: file, headers });
  if (!res.ok) {
    throw new Error(failMessage);
  }
  return instructions.avatarUrl;
}

async function uploadModuleHeroImageFile(
  accessToken: string,
  file: File,
  failMessage: string,
): Promise<string> {
  return uploadModuleImageFile(accessToken, file, failMessage, requestHeroImageUpload);
}

async function uploadModuleTrophyImageFile(
  accessToken: string,
  file: File,
  failMessage: string,
): Promise<string> {
  return uploadModuleImageFile(accessToken, file, failMessage, requestTrophyImageUpload);
}

function mergeLocLanguage(
  field: LocFieldDraft,
  lang: string,
  definitions: Record<string, string>,
): LocFieldDraft {
  if (field.dirtyLangs.includes(lang)) return field;
  const nextVal = definitions[field.key] ?? field.valuesByLang[lang] ?? "";
  if ((field.valuesByLang[lang] ?? "") === nextVal) return field;
  return {
    ...field,
    valuesByLang: {
      ...field.valuesByLang,
      [lang]: nextVal,
    },
  };
}

/** Soft-merge remote trainings (order / membership) without discarding local row identity. */
function mergeRemoteTrainings(
  local: CertificationTraining[],
  remote: CertificationTraining[],
): CertificationTraining[] {
  const localById = new Map(local.map((t) => [t.id, t]));
  let changed = local.length !== remote.length;
  const next = remote.map((rt, index) => {
    const prev = localById.get(rt.id);
    if (!prev) {
      changed = true;
      return rt;
    }
    if (
      prev.sortOrder !== rt.sortOrder
      || prev.stepNumber !== rt.stepNumber
      || prev.title !== rt.title
      || prev.section !== rt.section
      || prev.transcript !== rt.transcript
      || prev.videoCommentary !== rt.videoCommentary
      || prev.videoUrl !== rt.videoUrl
      || prev.thumbnailUrl !== rt.thumbnailUrl
    ) {
      changed = true;
      return {
        ...prev,
        sortOrder: rt.sortOrder,
        stepNumber: rt.stepNumber,
        title: rt.title,
        section: rt.section,
        transcript: rt.transcript,
        videoCommentary: rt.videoCommentary,
        videoUrl: rt.videoUrl,
        thumbnailUrl: rt.thumbnailUrl,
      };
    }
    // Detect reordered membership even when fields match (same set, different sequence).
    if (local[index]?.id !== rt.id) changed = true;
    return prev;
  });
  if (!changed) {
    for (const t of local) {
      if (!remote.some((rt) => rt.id === t.id)) {
        changed = true;
        break;
      }
    }
  }
  return changed ? next : local;
}

export function ModuleDetailPage({
  moduleId,
  trainingId: trainingIdFromSearch,
  questionId: questionIdFromSearch,
}: {
  moduleId: string;
  trainingId?: string;
  questionId?: string;
}) {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { t } = useI18n();
  const { resolve, language } = useCertificationContentDefinitions();
  const token = accessToken ?? getAuthCookie("access_token");
  const {
    languages: locLanguages,
    defaultLang,
    defsByLang,
    ready: locReady,
    remoteRevision,
    ensureLangDefinitions,
  } = useCertificationLocLanguages(token);
  const trainingRemoteRevision = useCertificationModuleRemoteRevision(moduleId);
  const { confirm: confirmTraining, ConfirmModal: TrainingConfirmModal } = useConfirmModal();
  const { confirm: confirmActive, ConfirmModal: ActiveConfirmModal } = useConfirmModal();
  const { isExpanded, toggle, bindSectionLayout } = useSectionCollapse(`certification-module-${moduleId}`);

  const [module, setModule] = React.useState<CertificationModuleDetail | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [courseValues, setCourseValues] = React.useState<CourseFormValues | null>(null);
  const [savedCourseValues, setSavedCourseValues] = React.useState<CourseFormValues | null>(null);
  const [titleLoc, setTitleLoc] = React.useState<LocFieldDraft | null>(null);
  const [descriptionLoc, setDescriptionLoc] = React.useState<LocFieldDraft | null>(null);
  const [certificateHeaderTextLoc, setCertificateHeaderTextLoc] = React.useState<LocFieldDraft | null>(null);
  const [certificateCourseTextLoc, setCertificateCourseTextLoc] = React.useState<LocFieldDraft | null>(null);
  const [savedTitleLoc, setSavedTitleLoc] = React.useState<LocFieldDraft | null>(null);
  const [savedDescriptionLoc, setSavedDescriptionLoc] = React.useState<LocFieldDraft | null>(null);
  const [savedCertificateHeaderTextLoc, setSavedCertificateHeaderTextLoc] = React.useState<LocFieldDraft | null>(null);
  const [savedCertificateCourseTextLoc, setSavedCertificateCourseTextLoc] = React.useState<LocFieldDraft | null>(null);
  const [courseFieldLang, setCourseFieldLang] = React.useState<Record<string, string>>({});
  const [courseFlashKeys, setCourseFlashKeys] = React.useState<Record<string, number>>({});
  const courseLocHydrationRef = React.useRef("");
  const [courseSaving, setCourseSaving] = React.useState(false);
  const [courseSaveError, setCourseSaveError] = React.useState<string | null>(null);
  const [heroError, setHeroError] = React.useState<string | null>(null);
  const [trophyError, setTrophyError] = React.useState<string | null>(null);

  const [trainingModalOpen, setTrainingModalOpen] = React.useState(false);
  const [selectedTrainingId, setSelectedTrainingId] = React.useState<string | null>(
    () => trainingIdFromSearch ?? null,
  );
  const [trainingsReordering, setTrainingsReordering] = React.useState(false);
  const [trainingEditor, setTrainingEditor] = React.useState<CourseTrainingEditorState | null>(null);
  const trainingDraftsRef = React.useRef<Record<string, TrainingDraftSnapshot>>({});
  const [trainingUnsavedCounts, setTrainingUnsavedCounts] = React.useState<Record<string, number>>({});
  /** Once true, never replace the page with a loading spinner (avoids editor remount / scroll jump). */
  const [hasShownContent, setHasShownContent] = React.useState(false);
  const titleLocRef = React.useRef(titleLoc);
  const descriptionLocRef = React.useRef(descriptionLoc);
  const certificateHeaderTextLocRef = React.useRef(certificateHeaderTextLoc);
  const certificateCourseTextLocRef = React.useRef(certificateCourseTextLoc);
  const moduleRef = React.useRef(module);
  const savedCourseValuesRef = React.useRef(savedCourseValues);
  titleLocRef.current = titleLoc;
  descriptionLocRef.current = descriptionLoc;
  certificateHeaderTextLocRef.current = certificateHeaderTextLoc;
  certificateCourseTextLocRef.current = certificateCourseTextLoc;
  moduleRef.current = module;
  savedCourseValuesRef.current = savedCourseValues;

  const updateSelectionSearch = React.useCallback(
    (patch: { trainingId?: string | null; questionId?: string | null }) => {
      void navigate({
        to: "/system/certifications/$moduleId",
        params: { moduleId },
        search: (prev) => {
          const next: { trainingId?: string; questionId?: string } = { ...prev };
          if (patch.trainingId !== undefined) {
            if (patch.trainingId) next.trainingId = patch.trainingId;
            else delete next.trainingId;
          }
          if (patch.questionId !== undefined) {
            if (patch.questionId) next.questionId = patch.questionId;
            else delete next.questionId;
          }
          // Avoid no-op navigations that remount route state.
          if (next.trainingId === prev.trainingId && next.questionId === prev.questionId) {
            return prev;
          }
          return next;
        },
        replace: true,
      });
    },
    [navigate, moduleId],
  );

  const selectTraining = React.useCallback(
    (trainingId: string | null) => {
      setSelectedTrainingId((prev) => {
        if (prev !== trainingId) {
          updateSelectionSearch({ trainingId, questionId: null });
        } else {
          updateSelectionSearch({ trainingId });
        }
        return trainingId;
      });
    },
    [updateSelectionSearch],
  );

  const selectQuestion = React.useCallback(
    (questionId: string | null) => {
      updateSelectionSearch({ questionId });
    },
    [updateSelectionSearch],
  );

  const load = React.useCallback((opts?: { soft?: boolean }) => {
    if (!token) return;
    const soft = Boolean(opts?.soft && moduleRef.current);
    if (!soft) setLoading(true);
    fetchCertificationModule(token, moduleId)
      .then((data) => {
        setModule(data);
        const form = moduleToCourseForm(data);
        setCourseValues((prev) => {
          const saved = savedCourseValuesRef.current;
          if (soft && prev && saved) {
            const dirty =
              prev.isActive !== saved.isActive
              || prev.slug !== saved.slug
              || prev.heroImageUrl !== saved.heroImageUrl
              || prev.trophyImageUrl !== saved.trophyImageUrl;
            if (dirty) return prev;
          }
          return form;
        });
        setSavedCourseValues(form);
        setCourseSaveError(null);
      })
      .catch((err) => setError(err?.message || t("pro.system.modules.error.load")))
      .finally(() => {
        if (!soft) setLoading(false);
      });
  }, [token, moduleId, t]);

  React.useEffect(() => { load(); }, [load]);

  // Soft-merge remote course localization into clean fields.
  React.useEffect(() => {
    if (!locReady || remoteRevision <= 0) return;
    const flash: string[] = [];
    for (const [lang, definitions] of Object.entries(defsByLang)) {
      const beforeTitle = titleLocRef.current;
      const beforeDesc = descriptionLocRef.current;
      const beforeCertHeader = certificateHeaderTextLocRef.current;
      const beforeCertCourse = certificateCourseTextLocRef.current;
      if (beforeTitle) {
        const after = mergeLocLanguage(beforeTitle, lang, definitions);
        if (
          after !== beforeTitle
          && !beforeTitle.dirtyLangs.includes(lang)
          && (beforeTitle.valuesByLang[lang] ?? "") !== (after.valuesByLang[lang] ?? "")
        ) {
          flash.push(beforeTitle.key);
        }
      }
      if (beforeDesc) {
        const after = mergeLocLanguage(beforeDesc, lang, definitions);
        if (
          after !== beforeDesc
          && !beforeDesc.dirtyLangs.includes(lang)
          && (beforeDesc.valuesByLang[lang] ?? "") !== (after.valuesByLang[lang] ?? "")
        ) {
          flash.push(beforeDesc.key);
        }
      }
      if (beforeCertHeader) {
        const after = mergeLocLanguage(beforeCertHeader, lang, definitions);
        if (
          after !== beforeCertHeader
          && !beforeCertHeader.dirtyLangs.includes(lang)
          && (beforeCertHeader.valuesByLang[lang] ?? "") !== (after.valuesByLang[lang] ?? "")
        ) {
          flash.push(beforeCertHeader.key);
        }
      }
      if (beforeCertCourse) {
        const after = mergeLocLanguage(beforeCertCourse, lang, definitions);
        if (
          after !== beforeCertCourse
          && !beforeCertCourse.dirtyLangs.includes(lang)
          && (beforeCertCourse.valuesByLang[lang] ?? "") !== (after.valuesByLang[lang] ?? "")
        ) {
          flash.push(beforeCertCourse.key);
        }
      }
      setTitleLoc((prev) => (prev ? mergeLocLanguage(prev, lang, definitions) : prev));
      setDescriptionLoc((prev) => (prev ? mergeLocLanguage(prev, lang, definitions) : prev));
      setCertificateHeaderTextLoc((prev) => (prev ? mergeLocLanguage(prev, lang, definitions) : prev));
      setCertificateCourseTextLoc((prev) => (prev ? mergeLocLanguage(prev, lang, definitions) : prev));
      setSavedTitleLoc((prev) => (prev ? mergeLocLanguage(prev, lang, definitions) : prev));
      setSavedDescriptionLoc((prev) => (prev ? mergeLocLanguage(prev, lang, definitions) : prev));
      setSavedCertificateHeaderTextLoc((prev) => (prev ? mergeLocLanguage(prev, lang, definitions) : prev));
      setSavedCertificateCourseTextLoc((prev) => (prev ? mergeLocLanguage(prev, lang, definitions) : prev));
    }
    if (flash.length > 0) {
      setCourseFlashKeys((prev) => {
        const next = { ...prev };
        for (const key of flash) next[key] = (prev[key] ?? 0) + 1;
        return next;
      });
    }
    // Do not soft-reload the whole module here — that remounts the training editor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteRevision]);

  // Soft-refresh module shell fields + training order from module WS.
  React.useEffect(() => {
    if (!token || trainingRemoteRevision <= 0) return;
    let cancelled = false;
    void fetchCertificationModule(token, moduleId)
      .then((data) => {
        if (cancelled) return;
        const nextHero = data.heroImageUrl ?? "";
        const nextTrophy = data.trophyImageUrl ?? "";
        const nextSlug = data.slug;
        const nextActive = data.isActive;

        setModule((prev) => {
          if (!prev) return data;
          const nextTrainings = mergeRemoteTrainings(prev.trainings, data.trainings ?? []);
          const shellChanged =
            (prev.heroImageUrl ?? "") !== nextHero
            || (prev.trophyImageUrl ?? "") !== nextTrophy
            || prev.slug !== nextSlug
            || prev.isActive !== nextActive
            || prev.title !== data.title
            || (prev.description ?? "") !== (data.description ?? "")
            || (prev.certificateHeaderText ?? "") !== (data.certificateHeaderText ?? "")
            || (prev.certificateCourseText ?? "") !== (data.certificateCourseText ?? "");
          if (!shellChanged && nextTrainings === prev.trainings) return prev;
          return {
            ...prev,
            heroImageUrl: data.heroImageUrl,
            trophyImageUrl: data.trophyImageUrl,
            slug: data.slug,
            isActive: data.isActive,
            title: data.title,
            description: data.description,
            certificateHeaderText: data.certificateHeaderText,
            certificateCourseText: data.certificateCourseText,
            trainings: nextTrainings,
          };
        });

        setCourseValues((prev) => {
          if (!prev) return moduleToCourseForm(data);
          const saved = savedCourseValuesRef.current;
          const heroDirty = Boolean(saved && prev.heroImageUrl !== saved.heroImageUrl);
          const trophyDirty = Boolean(saved && prev.trophyImageUrl !== saved.trophyImageUrl);
          const slugDirty = Boolean(saved && prev.slug !== saved.slug);
          const activeDirty = Boolean(saved && prev.isActive !== saved.isActive);
          const next = {
            ...prev,
            heroImageUrl: heroDirty ? prev.heroImageUrl : nextHero,
            trophyImageUrl: trophyDirty ? prev.trophyImageUrl : nextTrophy,
            slug: slugDirty ? prev.slug : nextSlug,
            isActive: activeDirty ? prev.isActive : nextActive,
          };
          if (
            next.heroImageUrl === prev.heroImageUrl
            && next.trophyImageUrl === prev.trophyImageUrl
            && next.slug === prev.slug
            && next.isActive === prev.isActive
          ) {
            return prev;
          }
          return next;
        });

        setSavedCourseValues((prev) => {
          if (!prev) return moduleToCourseForm(data);
          if (
            prev.heroImageUrl === nextHero
            && prev.trophyImageUrl === nextTrophy
            && prev.slug === nextSlug
            && prev.isActive === nextActive
          ) {
            return prev;
          }
          return {
            ...prev,
            heroImageUrl: nextHero,
            trophyImageUrl: nextTrophy,
            slug: nextSlug,
            isActive: nextActive,
          };
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [trainingRemoteRevision, token, moduleId]);

  React.useEffect(() => {
    if (!module || !locReady) return;
    const hydrationKey = [
      module.id,
      module.title,
      module.description ?? "",
      module.certificateHeaderText ?? "",
      module.certificateCourseText ?? "",
      defaultLang,
    ].join(":");
    if (courseLocHydrationRef.current === hydrationKey) return;
    const definitions = defsByLang[defaultLang];
    const nextTitle = locDraftFromStored(
      module.title,
      moduleTitleKey(module.id),
      defaultLang,
      definitions,
    );
    const nextDescription = locDraftFromStored(
      module.description,
      moduleDescriptionKey(module.id),
      defaultLang,
      definitions,
    );
    const nextCertificateHeaderText = locDraftFromStored(
      module.certificateHeaderText,
      moduleCertificateHeaderTextKey(module.id),
      defaultLang,
      definitions,
    );
    const nextCertificateCourseText = locDraftFromStored(
      module.certificateCourseText,
      moduleCertificateCourseTextKey(module.id),
      defaultLang,
      definitions,
    );
    courseLocHydrationRef.current = hydrationKey;
    setTitleLoc(nextTitle);
    setDescriptionLoc(nextDescription);
    setCertificateHeaderTextLoc(nextCertificateHeaderText);
    setCertificateCourseTextLoc(nextCertificateCourseText);
    setSavedTitleLoc(nextTitle);
    setSavedDescriptionLoc(nextDescription);
    setSavedCertificateHeaderTextLoc(nextCertificateHeaderText);
    setSavedCertificateCourseTextLoc(nextCertificateCourseText);
    setCourseFieldLang({});
  }, [module, locReady, defaultLang, defsByLang]);

  const patchCourse = React.useCallback((patch: Partial<CourseFormValues>) => {
    setCourseValues((prev) => (prev ? { ...prev, ...patch } : prev));
    setCourseSaveError(null);
  }, []);

  const handleActiveChange = React.useCallback(
    async (nextActive: boolean) => {
      if (!courseValues || nextActive === courseValues.isActive) return;
      const confirmed = await confirmActive(
        t(
          nextActive
            ? "pro.system.modules.confirm.activateTitle"
            : "pro.system.modules.confirm.deactivateTitle",
        ),
        t(
          nextActive
            ? "pro.system.modules.confirm.activateBody"
            : "pro.system.modules.confirm.deactivateBody",
        ),
        t(
          nextActive
            ? "pro.system.modules.confirm.activate"
            : "pro.system.modules.confirm.deactivate",
        ),
        nextActive ? "primary" : "danger",
      );
      if (confirmed) patchCourse({ isActive: nextActive });
    },
    [confirmActive, courseValues, patchCourse, t],
  );

  const activeCourseLangFor = React.useCallback(
    (key: string) => courseFieldLang[key] ?? defaultLang,
    [courseFieldLang, defaultLang],
  );

  const handleCourseFieldLangChange = React.useCallback(
    (key: string, lang: string) => {
      setCourseFieldLang((prev) => ({ ...prev, [key]: lang }));
      void ensureLangDefinitions(lang).then((definitions) => {
        setTitleLoc((prev) => prev ? mergeLocLanguage(prev, lang, definitions) : prev);
        setDescriptionLoc((prev) => prev ? mergeLocLanguage(prev, lang, definitions) : prev);
        setCertificateHeaderTextLoc((prev) => prev ? mergeLocLanguage(prev, lang, definitions) : prev);
        setCertificateCourseTextLoc((prev) => prev ? mergeLocLanguage(prev, lang, definitions) : prev);
        setSavedTitleLoc((prev) => prev ? mergeLocLanguage(prev, lang, definitions) : prev);
        setSavedDescriptionLoc((prev) => prev ? mergeLocLanguage(prev, lang, definitions) : prev);
        setSavedCertificateHeaderTextLoc((prev) => prev ? mergeLocLanguage(prev, lang, definitions) : prev);
        setSavedCertificateCourseTextLoc((prev) => prev ? mergeLocLanguage(prev, lang, definitions) : prev);
      });
    },
    [ensureLangDefinitions],
  );

  const heroImageItems: MediaEntry[] = React.useMemo(
    () =>
      courseValues?.heroImageUrl
        ? [{ id: moduleId, url: courseValues.heroImageUrl }]
        : [],
    [courseValues?.heroImageUrl, moduleId],
  );

  const trophyImageItems: MediaEntry[] = React.useMemo(
    () =>
      courseValues?.trophyImageUrl
        ? [{ id: `${moduleId}-trophy`, url: courseValues.trophyImageUrl }]
        : [],
    [courseValues?.trophyImageUrl, moduleId],
  );

  const handleHeroImageApply = React.useCallback(
    async (next: MediaEntry[]) => {
      if (!token || !courseValues) return;
      const url = next[0]?.url ?? "";
      patchCourse({ heroImageUrl: url });
      suppressCertificationModuleRemoteRevision(2500);
      const updated = await updateCertificationModule(token, moduleId, {
        heroImageUrl: url || null,
      });
      setModule((prev) => (prev ? { ...prev, ...updated } : prev));
      setSavedCourseValues((prev) => (prev ? { ...prev, heroImageUrl: url } : prev));
    },
    [token, courseValues, moduleId, patchCourse],
  );

  const handleHeroImageRemove = React.useCallback(async () => {
    if (!token) return;
    patchCourse({ heroImageUrl: "" });
    suppressCertificationModuleRemoteRevision(2500);
    const updated = await updateCertificationModule(token, moduleId, { heroImageUrl: null });
    setModule((prev) => (prev ? { ...prev, ...updated } : prev));
    setSavedCourseValues((prev) => (prev ? { ...prev, heroImageUrl: "" } : prev));
  }, [token, moduleId, patchCourse]);

  const handleTrophyImageApply = React.useCallback(
    async (next: MediaEntry[]) => {
      if (!token || !courseValues) return;
      const url = next[0]?.url ?? "";
      patchCourse({ trophyImageUrl: url });
      suppressCertificationModuleRemoteRevision(2500);
      const updated = await updateCertificationModule(token, moduleId, {
        trophyImageUrl: url || null,
      });
      setModule((prev) => (prev ? { ...prev, ...updated } : prev));
      setSavedCourseValues((prev) => (prev ? { ...prev, trophyImageUrl: url } : prev));
    },
    [token, courseValues, moduleId, patchCourse],
  );

  const handleTrophyImageRemove = React.useCallback(async () => {
    if (!token) return;
    patchCourse({ trophyImageUrl: "" });
    suppressCertificationModuleRemoteRevision(2500);
    const updated = await updateCertificationModule(token, moduleId, { trophyImageUrl: null });
    setModule((prev) => (prev ? { ...prev, ...updated } : prev));
    setSavedCourseValues((prev) => (prev ? { ...prev, trophyImageUrl: "" } : prev));
  }, [token, moduleId, patchCourse]);

  const activeChanged = Boolean(
    courseValues
    && savedCourseValues
    && courseValues.isActive !== savedCourseValues.isActive
  );
  const titleChanged = Boolean(titleLoc?.dirtyLangs.length);
  const descriptionChanged = Boolean(descriptionLoc?.dirtyLangs.length);
  const certificateHeaderTextChanged = Boolean(certificateHeaderTextLoc?.dirtyLangs.length);
  const certificateCourseTextChanged = Boolean(certificateCourseTextLoc?.dirtyLangs.length);
  const changeCount =
    Number(titleChanged)
    + Number(descriptionChanged)
    + Number(certificateHeaderTextChanged)
    + Number(certificateCourseTextChanged)
    + Number(activeChanged);
  const courseDirty = changeCount > 0;
  const trainingUnsavedTotal = Object.values(trainingUnsavedCounts).reduce(
    (sum, n) => sum + n,
    0,
  );
  const totalChangeCount = changeCount + trainingUnsavedTotal;
  const anyDirty = courseDirty || trainingUnsavedTotal > 0;
  const anySaving = courseSaving || Boolean(trainingEditor?.saving);

  const handleResetCourse = React.useCallback(() => {
    if (
      !savedCourseValues
      || !savedTitleLoc
      || !savedDescriptionLoc
      || !savedCertificateHeaderTextLoc
      || !savedCertificateCourseTextLoc
    ) {
      return;
    }
    setCourseValues(savedCourseValues);
    setTitleLoc(savedTitleLoc);
    setDescriptionLoc(savedDescriptionLoc);
    setCertificateHeaderTextLoc(savedCertificateHeaderTextLoc);
    setCertificateCourseTextLoc(savedCertificateCourseTextLoc);
    setCourseSaveError(null);
  }, [
    savedCourseValues,
    savedTitleLoc,
    savedDescriptionLoc,
    savedCertificateHeaderTextLoc,
    savedCertificateCourseTextLoc,
  ]);

  const handleResetAll = React.useCallback(() => {
    handleResetCourse();
    trainingEditor?.reset();
    trainingDraftsRef.current = {};
    setTrainingUnsavedCounts({});
  }, [handleResetCourse, trainingEditor]);

  const handleSaveCourse = React.useCallback(async () => {
    if (
      !token
      || !courseValues
      || !titleLoc
      || !descriptionLoc
      || !certificateHeaderTextLoc
      || !certificateCourseTextLoc
    ) {
      return;
    }
    if (!getLocDisplayValue(titleLoc, defaultLang, defaultLang).trim()) {
      setCourseSaveError(t("pro.system.modules.validation.titleRequired"));
      return;
    }
    setCourseSaving(true);
    setCourseSaveError(null);
    try {
      const titleForSave = ensureLocMigrationDirty(
        titleLoc,
        module?.title,
        defaultLang,
      );
      const descriptionForSave = ensureLocMigrationDirty(
        descriptionLoc,
        module?.description,
        defaultLang,
      );
      const certificateHeaderTextForSave = ensureLocMigrationDirty(
        certificateHeaderTextLoc,
        module?.certificateHeaderText,
        defaultLang,
      );
      const certificateCourseTextForSave = ensureLocMigrationDirty(
        certificateCourseTextLoc,
        module?.certificateCourseText,
        defaultLang,
      );
      const updates = collectDirtyUpdates([
        titleForSave,
        descriptionForSave,
        certificateHeaderTextForSave,
        certificateCourseTextForSave,
      ]).filter((update) => Object.keys(update.set).length > 0);
      if (updates.length > 0) {
        suppressCertificationLocRemoteRevision(2500);
        await batchPatchLocalizationDefinitions(token, {
          context: "certification",
          updates,
        });
      }
      suppressCertificationModuleRemoteRevision(2500);
      const updated = await updateCertificationModule(token, moduleId, {
        title: titleForSave.key,
        description: getLocDisplayValue(
          descriptionForSave,
          defaultLang,
          defaultLang,
        ).trim()
          ? descriptionForSave.key
          : null,
        certificateHeaderText: getLocDisplayValue(
          certificateHeaderTextForSave,
          defaultLang,
          defaultLang,
        ).trim()
          ? certificateHeaderTextForSave.key
          : null,
        certificateCourseText: getLocDisplayValue(
          certificateCourseTextForSave,
          defaultLang,
          defaultLang,
        ).trim()
          ? certificateCourseTextForSave.key
          : null,
        isActive: courseValues.isActive,
      });
      const nextForm = moduleToCourseForm({
        ...(module as CertificationModuleDetail),
        ...updated,
        trainings: module?.trainings ?? [],
      });
      const nextTitleLoc = { ...titleForSave, dirtyLangs: [] };
      const nextDescriptionLoc = { ...descriptionForSave, dirtyLangs: [] };
      const nextCertificateHeaderTextLoc = { ...certificateHeaderTextForSave, dirtyLangs: [] };
      const nextCertificateCourseTextLoc = { ...certificateCourseTextForSave, dirtyLangs: [] };
      courseLocHydrationRef.current = [
        moduleId,
        updated.title,
        updated.description ?? "",
        updated.certificateHeaderText ?? "",
        updated.certificateCourseText ?? "",
        defaultLang,
      ].join(":");
      for (const update of updates) {
        invalidateCertificationContentDefinitionsCache(update.lang);
      }
      setModule((prev) => (prev ? { ...prev, ...updated } : prev));
      setCourseValues(nextForm);
      setSavedCourseValues(nextForm);
      setTitleLoc(nextTitleLoc);
      setDescriptionLoc(nextDescriptionLoc);
      setCertificateHeaderTextLoc(nextCertificateHeaderTextLoc);
      setCertificateCourseTextLoc(nextCertificateCourseTextLoc);
      setSavedTitleLoc(nextTitleLoc);
      setSavedDescriptionLoc(nextDescriptionLoc);
      setSavedCertificateHeaderTextLoc(nextCertificateHeaderTextLoc);
      setSavedCertificateCourseTextLoc(nextCertificateCourseTextLoc);
    } catch (err: any) {
      setCourseSaveError(err?.message ?? t("pro.system.modules.error.save"));
    } finally {
      setCourseSaving(false);
    }
  }, [
    token,
    courseValues,
    titleLoc,
    descriptionLoc,
    certificateHeaderTextLoc,
    certificateCourseTextLoc,
    defaultLang,
    moduleId,
    module,
    t,
  ]);

  const handleTrainingSaved = React.useCallback((saved: CertificationTraining) => {
    // Defer cache invalidation until after local dirty clear + paint, so strip
    // titles refresh without tearing down the editor mid-save.
    window.setTimeout(() => {
      invalidateCertificationContentDefinitionsCache(language);
      invalidateCertificationContentDefinitionsCache("en");
    }, 0);
    setSelectedTrainingId((prev) => prev ?? saved.id);
    setModule((prev) => {
      if (!prev) return prev;
      const idx = prev.trainings.findIndex((training) => training.id === saved.id);
      if (idx < 0) {
        return { ...prev, trainings: [...prev.trainings, saved] };
      }
      const existing = prev.trainings[idx];
      // Skip state update when display-relevant fields are unchanged — a new
      // training object identity was remounting the editor via the slider.
      if (
        existing.title === saved.title
        && existing.section === saved.section
        && existing.transcript === saved.transcript
        && existing.videoCommentary === saved.videoCommentary
        && existing.videoUrl === saved.videoUrl
        && existing.thumbnailUrl === saved.thumbnailUrl
        && existing.sortOrder === saved.sortOrder
        && existing.stepNumber === saved.stepNumber
      ) {
        return prev;
      }
      const next = [...prev.trainings];
      next[idx] = { ...existing, ...saved };
      return { ...prev, trainings: next };
    });
  }, [language]);

  const clearTrainingDraft = React.useCallback((trainingId: string) => {
    delete trainingDraftsRef.current[trainingId];
    setTrainingUnsavedCounts((prev) => {
      if (!(trainingId in prev)) return prev;
      const next = { ...prev };
      delete next[trainingId];
      return next;
    });
  }, []);

  const trainingEditorRef = React.useRef(trainingEditor);
  trainingEditorRef.current = trainingEditor;
  const ignoreDirtyUntilRef = React.useRef<Record<string, number>>({});
  const trainingUnsavedCountsRef = React.useRef(trainingUnsavedCounts);
  trainingUnsavedCountsRef.current = trainingUnsavedCounts;

  React.useEffect(() => {
    if (module && courseValues && locReady && titleLoc && descriptionLoc) {
      setHasShownContent(true);
    }
  }, [module, courseValues, locReady, titleLoc, descriptionLoc]);

  const handleSaveAll = React.useCallback(async () => {
    const editor = trainingEditorRef.current;
    const selectedId = selectedTrainingId;
    const counts = trainingUnsavedCountsRef.current;
    const selectedCount = selectedId ? (counts[selectedId] ?? 0) : 0;


    if (courseDirty) await handleSaveCourse();
    if (!token) {
      return;
    }

    // Always prefer the live editor for the selected training so local dirty
    // state is cleared (snapshot-only saves left the mounted editor dirty).
    if (
      selectedId
      && editor
      && (editor.dirty || editor.changeCount > 0 || selectedCount > 0)
    ) {
      ignoreDirtyUntilRef.current[selectedId] = Date.now() + 5000;
      const ok = await editor.save();
      if (ok) clearTrainingDraft(selectedId);
      else delete ignoreDirtyUntilRef.current[selectedId];
    } else {
    }

    const draftEntries = Object.entries(trainingDraftsRef.current);
    for (const [trainingId, snapshot] of draftEntries) {
      if (trainingId === selectedId) continue;
      if (!getLocDisplayValue(
        snapshot.fields.title,
        defaultLang,
        defaultLang,
      ).trim()) continue;
      const row = (module?.trainings ?? []).find((training) => training.id === trainingId);
      if (!row) continue;
      try {
        ignoreDirtyUntilRef.current[trainingId] = Date.now() + 5000;
        const updated = await persistTrainingTextDraft({
          accessToken: token,
          training: row,
          fields: snapshot.fields,
          questions: snapshot.questions,
          questionsDirty: isQuestionsDraftDirty(
            snapshot.questions,
            snapshot.savedQuestions,
          ),
          defaultLang,
        });
        handleTrainingSaved({
          ...updated,
          videoUrl: updated.videoUrl ?? row.videoUrl,
          thumbnailUrl: updated.thumbnailUrl ?? row.thumbnailUrl,
        });
        clearTrainingDraft(trainingId);
      } catch {
        delete ignoreDirtyUntilRef.current[trainingId];
        // Keep draft; selected editor surfaces errors when focused.
      }
    }

  }, [
    courseDirty,
    handleSaveCourse,
    token,
    selectedTrainingId,
    module?.trainings,
    defaultLang,
    handleTrainingSaved,
    clearTrainingDraft,
  ]);

  const floatingActions = React.useMemo((): FloatingAction[] => {
    if (!anyDirty) return [];
    return [
      {
        id: "reset",
        icon: <RotateCcw size={16} />,
        shape: "circle",
        tooltip: t("pro.productSettings.resetUnsaved"),
        disabled: anySaving,
        onClick: handleResetAll,
      },
      {
        id: "save",
        label:
          totalChangeCount === 1
            ? `Save ${totalChangeCount} change`
            : `Save ${totalChangeCount} changes`,
        icon: <Save size={16} />,
        shape: "rectangle",
        loading: anySaving,
        disabled: anySaving,
        onClick: () => void handleSaveAll(),
      },
    ];
  }, [anyDirty, totalChangeCount, anySaving, handleResetAll, handleSaveAll, t]);

  React.useEffect(() => {
    if (!anyDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [anyDirty]);

  const trainings = module?.trainings ?? [];

  const orderedTrainings = React.useMemo(
    () =>
      [...trainings].sort(
        (a, b) => a.sortOrder - b.sortOrder || a.stepNumber - b.stepNumber,
      ),
    [trainings],
  );
  const selectedTrainingIndex = orderedTrainings.findIndex(
    (training) => training.id === selectedTrainingId,
  );
  const selectedTraining =
    selectedTrainingIndex >= 0 ? orderedTrainings[selectedTrainingIndex] : null;

  React.useEffect(() => {
    if (!module) return;
    setSelectedTrainingId((prev) => {
      const fromSearch = trainingIdFromSearch;
      if (fromSearch && module.trainings.some((t) => t.id === fromSearch)) {
        return fromSearch;
      }
      if (prev && module.trainings.some((t) => t.id === prev)) {
        if (fromSearch !== prev) {
          updateSelectionSearch({ trainingId: prev });
        }
        return prev;
      }
      const ordered = [...module.trainings].sort(
        (a, b) => a.sortOrder - b.sortOrder || a.stepNumber - b.stepNumber,
      );
      const fallback = ordered[0]?.id ?? null;
      if (fallback) {
        updateSelectionSearch({ trainingId: fallback });
      }
      return fallback;
    });
  }, [module, trainingIdFromSearch, updateSelectionSearch]);

  const handleTrainingsReorder = React.useCallback(
    async (ordered: CertificationTraining[]) => {
      if (!token) return;
      const previous = trainings;
      const withSteps = ordered.map((training, index) => ({
        ...training,
        stepNumber: index + 1,
        sortOrder: index + 1,
      }));

      setModule((prev) => (prev ? { ...prev, trainings: withSteps } : prev));

      const changed = withSteps.filter((training) => {
        const original = previous.find((t) => t.id === training.id);
        return (
          !original ||
          original.stepNumber !== training.stepNumber ||
          original.sortOrder !== training.sortOrder
        );
      });
      if (changed.length === 0) return;

      setTrainingsReordering(true);
      try {
        suppressCertificationModuleRemoteRevision(2500);
        await Promise.all(
          changed.map((training) =>
            updateCertificationTraining(token, training.id, {
              stepNumber: training.stepNumber,
              sortOrder: training.sortOrder,
            }),
          ),
        );
      } catch {
        load({ soft: true });
      } finally {
        setTrainingsReordering(false);
      }
    },
    [token, trainings, load],
  );

  const handleTrainingDraftChange = React.useCallback(
    (trainingId: string, snapshot: TrainingDraftSnapshot, changeCount: number) => {
      if (
        changeCount > 0
        && Date.now() < (ignoreDirtyUntilRef.current[trainingId] ?? 0)
      ) {
        return;
      }
      if (changeCount === 0) {
        delete ignoreDirtyUntilRef.current[trainingId];
        delete trainingDraftsRef.current[trainingId];
      } else {
        trainingDraftsRef.current[trainingId] = snapshot;
      }
      setTrainingUnsavedCounts((prev) => {
        const current = prev[trainingId] ?? 0;
        if (changeCount === 0) {
          if (!(trainingId in prev)) return prev;
          const next = { ...prev };
          delete next[trainingId];
          return next;
        }
        if (current === changeCount) return prev;
        return { ...prev, [trainingId]: changeCount };
      });
    },
    [],
  );

  const handleDeleteTraining = React.useCallback(
    async (row: CertificationTraining) => {
      if (!token) return;
      const label =
        resolve(row.title) ||
        (!isContentLocKey(row.title) ? row.title : t("pro.system.modules.confirm.thisTraining"));
      const confirmed = await confirmTraining(
        t("pro.system.modules.confirm.deleteTrainingTitle"),
        t("pro.system.modules.confirm.deleteTrainingBody", { title: label }),
        t("shared.common.delete"),
        "danger",
      );
      if (!confirmed) return;

      const previous = trainings;
      const remaining = [...trainings]
        .filter((training) => training.id !== row.id)
        .sort((a, b) => a.sortOrder - b.sortOrder || a.stepNumber - b.stepNumber)
        .map((training, index) => ({
          ...training,
          stepNumber: index + 1,
          sortOrder: index + 1,
        }));

      delete trainingDraftsRef.current[row.id];
      setTrainingUnsavedCounts((prev) => {
        if (!(row.id in prev)) return prev;
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
      setModule((prev) => (prev ? { ...prev, trainings: remaining } : prev));
      if (selectedTrainingId === row.id) {
        selectTraining(remaining[0]?.id ?? null);
      }

      setTrainingsReordering(true);
      try {
        suppressCertificationModuleRemoteRevision(2500);
        await deleteCertificationTraining(token, row.id);
        const changed = remaining.filter((training) => {
          const original = previous.find((t) => t.id === training.id);
          return (
            !original ||
            original.stepNumber !== training.stepNumber ||
            original.sortOrder !== training.sortOrder
          );
        });
        if (changed.length > 0) {
          await Promise.all(
            changed.map((training) =>
              updateCertificationTraining(token, training.id, {
                stepNumber: training.stepNumber,
                sortOrder: training.sortOrder,
              }),
            ),
          );
        }
      } catch {
        load({ soft: true });
      } finally {
        setTrainingsReordering(false);
      }
    },
    [
      token,
      trainings,
      selectedTrainingId,
      selectTraining,
      confirmTraining,
      resolve,
      t,
      load,
    ],
  );

  const handleBack = () => navigate({ to: "/system/certifications" });

  const openAddTraining = React.useCallback(() => {
    setTrainingModalOpen(true);
  }, []);

  if (
    !hasShownContent
    && (
      loading
      || (!error && module && courseValues && (
        !locReady
        || !titleLoc
        || !descriptionLoc
        || !certificateHeaderTextLoc
        || !certificateCourseTextLoc
      ))
    )
  ) {
    return (
      <div style={{ padding: "32px 24px" }}>
        <Text level="body-medium" style={{ color: "#9CA3AF" }}>{t("shared.common.loading")}</Text>
      </div>
    );
  }

  if (error || !module || !courseValues) {
    return (
      <DetailPageLayout
        backLink={{
          label: t("pro.system.certification.title"),
          onClick: handleBack,
        }}
        header={{
          media: <GraduationCap size={48} />,
          title: t("pro.system.modules.error.notFound"),
          subtitle: false,
        }}
      >
        <Callout variant="error">{error ?? t("pro.system.modules.error.notFound")}</Callout>
      </DetailPageLayout>
    );
  }

  // Narrowed after the loading guard above; keep an explicit check for tsc.
  if (!titleLoc || !descriptionLoc || !certificateHeaderTextLoc || !certificateCourseTextLoc) {
    return (
      <div style={{ padding: "32px 24px" }}>
        <Text level="body-medium" style={{ color: "#9CA3AF" }}>{t("shared.common.loading")}</Text>
      </div>
    );
  }

  const nextStepNumber = trainings.length + 1;
  const titleLang = activeCourseLangFor(titleLoc.key);
  const descriptionLang = activeCourseLangFor(descriptionLoc.key);
  const certificateHeaderTextLang = activeCourseLangFor(certificateHeaderTextLoc.key);
  const certificateCourseTextLang = activeCourseLangFor(certificateCourseTextLoc.key);
  const courseTitleDisplay = getLocDisplayValue(titleLoc, titleLang, defaultLang);
  const certificateHeaderTextDisplay = getLocDisplayValue(
    certificateHeaderTextLoc,
    certificateHeaderTextLang,
    defaultLang,
  );
  const certificateCourseTextDisplay = getLocDisplayValue(
    certificateCourseTextLoc,
    certificateCourseTextLang,
    defaultLang,
  );
  const courseDescriptionDisplay = getLocDisplayValue(
    descriptionLoc,
    descriptionLang,
    defaultLang,
  );

  const sectionLayout = bindSectionLayout([
    {
      id: SECTION_IDS.course,
      label: t("pro.system.modules.course.title"),
      icon: <LayoutTemplate size={14} />,
    },
  ]);

  const headerBadges: React.ReactNode[] = !module.isActive
    ? [
        <span
          key="inactive"
          title={t("pro.system.modules.inactiveBanner")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            fontSize: 13,
            fontWeight: 500,
            padding: "2px 10px",
            borderRadius: 99,
            background: "#F3F4F6",
            color: "#6B7280",
          }}
        >
          {t("pro.system.modules.badge.inactive")}
        </span>,
      ]
    : [];

  return (
    <>
      <DetailPageLayout
        backLink={{
          label: t("pro.system.certification.title"),
          onClick: handleBack,
        }}
        header={{
          media: (
            <div className={courseStyles.headerHero}>
              {courseValues.heroImageUrl ? (
                <img
                  src={courseValues.heroImageUrl}
                  alt=""
                  className={courseStyles.headerHeroImg}
                />
              ) : (
                <GraduationCap size={28} color="#9CA3AF" aria-hidden />
              )}
            </div>
          ),
          title: courseTitleDisplay,
          subtitle: courseDescriptionDisplay || false,
          badges: headerBadges,
        }}
        {...sectionLayout}
        floatingActions={floatingActions}
      >
        {courseSaveError && (
          <div style={{ marginBottom: 16 }}>
            <Callout variant="error">{courseSaveError}</Callout>
          </div>
        )}

        <CollapsibleSection
          id={SECTION_IDS.course}
          title={t("pro.system.modules.course.title")}
          subtitle={t("pro.system.modules.course.subtitle")}
          icon={<LayoutTemplate size={20} />}
          expanded={isExpanded(SECTION_IDS.course)}
          onToggleExpanded={() => toggle(SECTION_IDS.course)}
          highlight={anyDirty ? "accent" : false}
          statusIndicators={
            anyDirty
              ? [<UnsavedChangesChip key="u" count={totalChangeCount} />]
              : undefined
          }
        >
          <div className={layout.cardInner}>
            <div className={courseStyles.courseSectionBody}>
              <div className={courseStyles.courseForm}>
                <div className={courseStyles.titleSlugRow}>
                  <LocalizedField
                    label={t("pro.system.modules.form.title")}
                    locKey={titleLoc.key}
                    languages={locLanguages}
                    selectedLang={titleLang}
                    onSelectedLangChange={(lang) =>
                      handleCourseFieldLangChange(titleLoc.key, lang)
                    }
                    value={courseTitleDisplay}
                    referenceValue={titleLoc.valuesByLang[defaultLang]}
                    onChange={(lang, value) => {
                      setTitleLoc((prev) => prev ? setLocValue(prev, lang, value) : prev);
                      setCourseSaveError(null);
                    }}
                    onLanguageFocus={(lang) => void ensureLangDefinitions(lang)}
                    disabled={courseSaving}
                    required
                    placeholder={t("pro.system.modules.form.titlePlaceholder")}
                    defaultLang={defaultLang}
                    changed={titleChanged}
                    flashKey={courseFlashKeys[titleLoc.key] ?? 0}
                  />
                  <div className={courseStyles.fieldGroup}>
                    <span className={courseStyles.fieldLabel}>
                      {t("pro.system.modules.form.slug")}
                    </span>
                    <div
                      className={courseStyles.slugReadonly}
                      title={t("pro.system.modules.form.slugReadonlyHint")}
                    >
                      {courseValues.slug}
                    </div>
                  </div>
                </div>

                <div className={courseStyles.descriptionHeroRow}>
                  <LocalizedField
                    label={t("pro.system.modules.form.description")}
                    locKey={descriptionLoc.key}
                    languages={locLanguages}
                    selectedLang={descriptionLang}
                    onSelectedLangChange={(lang) =>
                      handleCourseFieldLangChange(descriptionLoc.key, lang)
                    }
                    value={courseDescriptionDisplay}
                    referenceValue={descriptionLoc.valuesByLang[defaultLang]}
                    onChange={(lang, value) => {
                      setDescriptionLoc((prev) =>
                        prev ? setLocValue(prev, lang, value) : prev
                      );
                      setCourseSaveError(null);
                    }}
                    onLanguageFocus={(lang) => void ensureLangDefinitions(lang)}
                    disabled={courseSaving}
                    multiline
                    rows={4}
                    placeholder={t("pro.system.modules.form.descriptionPlaceholder")}
                    defaultLang={defaultLang}
                    changed={descriptionChanged}
                    flashKey={courseFlashKeys[descriptionLoc.key] ?? 0}
                  />

                  <div className={courseStyles.fieldGroup}>
                    <label className={courseStyles.fieldLabel}>
                      {t("pro.system.modules.form.heroImage")}
                    </label>
                    <div className={courseStyles.heroUploader}>
                      <MediaUploader
                        mediaType="image"
                        items={heroImageItems}
                        onChange={() => {}}
                        onApply={handleHeroImageApply}
                        onRemove={handleHeroImageRemove}
                        uploadFile={(file) =>
                          uploadModuleHeroImageFile(
                            token ?? "",
                            file,
                            t("pro.system.modules.error.uploadHero"),
                          )
                        }
                        maxItems={1}
                        shape="rectangle"
                        aspectRatio={courseStyles.HERO_ASPECT}
                        placeholder={t("pro.system.modules.form.heroPlaceholder")}
                        error={heroError}
                        onError={setHeroError}
                      />
                    </div>
                  </div>
                  <div className={courseStyles.fieldGroup}>
                    <label className={courseStyles.fieldLabel}>
                      Trophy image
                    </label>
                    <div className={courseStyles.trophyUploader}>
                      <MediaUploader
                        mediaType="image"
                        items={trophyImageItems}
                        onChange={() => {}}
                        onApply={handleTrophyImageApply}
                        onRemove={handleTrophyImageRemove}
                        uploadFile={(file) =>
                          uploadModuleTrophyImageFile(
                            token ?? "",
                            file,
                            "Failed to upload trophy image",
                          )
                        }
                        maxItems={1}
                        shape="square"
                        previewSize={courseStyles.TROPHY_UPLOADER_SIZE}
                        placeholder={t("pro.system.modules.uploadTrophy")}
                        error={trophyError}
                        onError={setTrophyError}
                      />
                    </div>
                  </div>
                </div>

                <div className={courseStyles.certificateTextRow}>
                  <LocalizedField
                    label={t("pro.system.modules.form.certificateHeaderText")}
                    locKey={certificateHeaderTextLoc.key}
                    languages={locLanguages}
                    selectedLang={certificateHeaderTextLang}
                    onSelectedLangChange={(lang) =>
                      handleCourseFieldLangChange(certificateHeaderTextLoc.key, lang)
                    }
                    value={certificateHeaderTextDisplay}
                    referenceValue={certificateHeaderTextLoc.valuesByLang[defaultLang]}
                    onChange={(lang, value) => {
                      setCertificateHeaderTextLoc((prev) =>
                        prev ? setLocValue(prev, lang, value) : prev
                      );
                      setCourseSaveError(null);
                    }}
                    onLanguageFocus={(lang) => void ensureLangDefinitions(lang)}
                    disabled={courseSaving}
                    placeholder={t("pro.system.modules.form.certificateHeaderTextPlaceholder")}
                    defaultLang={defaultLang}
                    changed={certificateHeaderTextChanged}
                    flashKey={courseFlashKeys[certificateHeaderTextLoc.key] ?? 0}
                  />
                  <LocalizedField
                    label={t("pro.system.modules.form.certificateCourseText")}
                    locKey={certificateCourseTextLoc.key}
                    languages={locLanguages}
                    selectedLang={certificateCourseTextLang}
                    onSelectedLangChange={(lang) =>
                      handleCourseFieldLangChange(certificateCourseTextLoc.key, lang)
                    }
                    value={certificateCourseTextDisplay}
                    referenceValue={certificateCourseTextLoc.valuesByLang[defaultLang]}
                    onChange={(lang, value) => {
                      setCertificateCourseTextLoc((prev) =>
                        prev ? setLocValue(prev, lang, value) : prev
                      );
                      setCourseSaveError(null);
                    }}
                    onLanguageFocus={(lang) => void ensureLangDefinitions(lang)}
                    disabled={courseSaving}
                    placeholder={t("pro.system.modules.form.certificateCourseTextPlaceholder")}
                    defaultLang={defaultLang}
                    changed={certificateCourseTextChanged}
                    flashKey={courseFlashKeys[certificateCourseTextLoc.key] ?? 0}
                  />
                </div>

                <div className={courseStyles.activePreviewRow}>
                  <label
                    className={clsx(
                      courseStyles.checkboxLabel,
                      activeChanged && courseStyles.checkboxLabelChanged,
                    )}
                    data-unsaved={activeChanged ? "true" : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={courseValues.isActive}
                      onChange={(e) => void handleActiveChange(e.target.checked)}
                      disabled={courseSaving}
                    />
                    {t("pro.system.modules.form.active")}
                  </label>

                  <div className={courseStyles.previewIconGroup}>
                    <span className={courseStyles.previewIconsLabel}>
                      {t("pro.system.modules.course.previewsLabel")}
                    </span>
                    <CoursePreviewHoverButton
                      label={t("pro.system.modules.course.cardPreviewLabel")}
                      icon={<IdCard size={16} aria-hidden />}
                      previewClassName={courseStyles.previewPopoverCard}
                    >
                      <div className={courseStyles.coursePreviewCardWrap}>
                        <CertificationCourseCard
                          title={courseTitleDisplay}
                          heroImageUrl={courseValues.heroImageUrl || null}
                          slug={courseValues.slug}
                        />
                        {!courseValues.isActive ? (
                          <div className={courseStyles.inactiveOverlay}>
                            <span className={courseStyles.inactiveBadge}>
                              {t("pro.system.modules.badge.inactive")}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </CoursePreviewHoverButton>

                    <CoursePreviewHoverButton
                      label={t("pro.system.modules.course.pagePreviewLabel")}
                      icon={<PanelTop size={16} aria-hidden />}
                      previewClassName={courseStyles.previewPopoverCoursePage}
                    >
                      <CoursePagePreview
                        title={courseTitleDisplay}
                        heroImageUrl={courseValues.heroImageUrl || null}
                        trainings={orderedTrainings}
                      />
                      {!courseValues.isActive ? (
                        <div className={courseStyles.inactiveOverlay}>
                          <span className={courseStyles.inactiveBadge}>
                            {t("pro.system.modules.badge.inactive")}
                          </span>
                        </div>
                      ) : null}
                    </CoursePreviewHoverButton>

                    <CoursePreviewHoverButton
                      label={t("pro.system.modules.course.progressPreviewLabel")}
                      icon={<Award size={16} aria-hidden />}
                      previewClassName={courseStyles.previewPopoverProgress}
                    >
                      <div className={courseStyles.progressPreviewCardWrap}>
                        <ListViewUserCertificationCard
                          layout="stack"
                          certification={{
                            moduleId,
                            moduleTitle: courseTitleDisplay,
                            progressPercent: 0,
                            completedAt: null,
                            status: "not_started",
                            expiresAt: null,
                            trophyImageUrl: courseValues.trophyImageUrl || null,
                            heroImageUrl: courseValues.heroImageUrl || null,
                            trainings: orderedTrainings.map((training) => ({
                              id: training.id,
                              title: training.title,
                              section: training.section,
                              completed: false,
                              stepNumber: training.stepNumber,
                              videoUrl: training.videoUrl,
                              thumbnailUrl: training.thumbnailUrl,
                              questionCount: training.questions?.length,
                              videoDurationSeconds: training.videoDurationSeconds,
                            })),
                          }}
                        />
                        {!courseValues.isActive ? (
                          <div className={courseStyles.inactiveOverlay}>
                            <span className={courseStyles.inactiveBadge}>
                              {t("pro.system.modules.badge.inactive")}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </CoursePreviewHoverButton>

                    <CoursePreviewHoverButton
                      label={t("pro.system.modules.course.certificatePreviewLabel")}
                      icon={<ScrollText size={16} aria-hidden />}
                      previewClassName={courseStyles.previewPopoverCertificate}
                    >
                      <CertificatePreview
                        heading={certificateHeaderTextDisplay}
                        recipientName="Sample Trainee"
                        courseTitle={certificateCourseTextDisplay.trim() || courseTitleDisplay}
                        completedAt={new Date().toISOString()}
                        certificateNumber="SAMPLE-0000"
                      />
                    </CoursePreviewHoverButton>
                  </div>
                </div>
              </div>

              <CourseTrainingsStrip
                trainings={trainings}
                selectedId={selectedTrainingId}
                onSelect={(row) => selectTraining(row.id)}
                onReorder={(ordered) => void handleTrainingsReorder(ordered)}
                onAdd={openAddTraining}
                onDelete={(row) => void handleDeleteTraining(row)}
                unsavedCounts={trainingUnsavedCounts}
                reordering={trainingsReordering}
              />

              {selectedTraining && token ? (
                <CourseTrainingDetailSlider
                  training={selectedTraining}
                  index={selectedTrainingIndex}
                  accessToken={token}
                  onSaved={handleTrainingSaved}
                  onStateChange={setTrainingEditor}
                  draft={trainingDraftsRef.current[selectedTraining.id] ?? null}
                  onDraftChange={handleTrainingDraftChange}
                  languages={locLanguages}
                  defaultLang={defaultLang}
                  defsByLang={defsByLang}
                  locReady={locReady}
                  remoteRevision={remoteRevision}
                  trainingRemoteRevision={trainingRemoteRevision}
                  ensureLangDefinitions={ensureLangDefinitions}
                  selectedQuestionId={questionIdFromSearch ?? null}
                  onSelectedQuestionIdChange={selectQuestion}
                />
              ) : null}
            </div>
          </div>
        </CollapsibleSection>
      </DetailPageLayout>

      <TrainingFormModal
        open={trainingModalOpen}
        onClose={() => setTrainingModalOpen(false)}
        moduleId={moduleId}
        accessToken={token ?? ""}
        existing={undefined}
        nextStepNumber={nextStepNumber}
        onSaved={handleTrainingSaved}
      />

      <TrainingConfirmModal />
      <ActiveConfirmModal />
    </>
  );
}
